import type { TableProps } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';

import { AppButton, AppModal, AppSpin, AppTable } from '@/components/atoms';
import { useDuplicatePatients } from '@/hooks/main/usePatientCertification';
import { renderTableCellTruncate } from '@/utils/tableCellTruncate';
import type {
  ComparedPatient,
  FieldColor,
  FormDataForComparison,
} from '@/services/certification/types';

interface PatientDuplicateModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientIds: number[];
  /** Overwrite existing patient (update_type = 0). */
  onSelectPatient: (patientId: number) => void;
  /** Create new patient (patient_id = 0). */
  onCreateNew: () => void;
  /** Copy with history creation (update_type = 1). */
  onCopyWithHistory: (patientId: number) => void;
  formDataForComparison?: FormDataForComparison;
}

const CELL_CLASS: Record<FieldColor, string> = {
  normal: '',
  difference: 'cert-cell-difference',
  change_error: 'cert-cell-change-error',
};

const cellClass = (color: FieldColor | undefined) => CELL_CLASS[color ?? 'normal'];

const sexLabel = (sexId: number) => (sexId === 1 ? '男' : sexId === 2 ? '女' : '');

const PatientDuplicateModal: React.FC<PatientDuplicateModalProps> = ({
  isOpen,
  onClose,
  patientIds,
  onSelectPatient,
  onCreateNew,
  onCopyWithHistory,
  formDataForComparison,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const { data: patients = [], isLoading } = useDuplicatePatients(
    patientIds,
    formDataForComparison,
    isOpen
  );

  useEffect(() => {
    if (!isOpen) setSelectedRowKeys([]);
  }, [isOpen]);

  const selectedPatient = useMemo(
    () => patients.find(p => p.id === selectedRowKeys[0]),
    [patients, selectedRowKeys]
  );

  // 履歴作成 is disabled when there is no selection, a change_error overlap exists,
  // or the patient is not an official record.
  const isHistoryDisabled =
    selectedRowKeys.length === 0 ||
    selectedPatient?.change_error === true ||
    selectedPatient?.official === 0;

  const columns: NonNullable<TableProps<ComparedPatient>['columns']> = [
    {
      title: '受診者番号',
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: 100,
      align: 'left',
    },
    {
      title: '受診者名',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text) => renderTableCellTruncate(text, { width: 150, lineClamp: 2 }),
      onCell: r => ({ className: cellClass(r.colors?.name) }),
    },
    {
      title: '生年月日',
      dataIndex: 'date_of_birth',
      key: 'date_of_birth',
      width: 130,
      align: 'left',
      onCell: r => ({ className: cellClass(r.colors?.date_of_birth) }),
    },
    {
      title: '性別',
      key: 'sex_id',
      width: 60,
      align: 'left',
      render: (_, record) => sexLabel(record.sex_id),
      onCell: r => ({ className: cellClass(r.colors?.sex) }),
    },
    {
      title: '記号',
      dataIndex: 'card_mark',
      key: 'card_mark',
      width: 80,
      onCell: r => ({ className: cellClass(r.colors?.card_mark) }),
    },
    {
      title: '番号',
      dataIndex: 'card_number',
      key: 'card_number',
      width: 80,
      onCell: r => ({ className: cellClass(r.colors?.card_number) }),
    },
    {
      title: '保険者番号',
      dataIndex: 'insurer_number',
      key: 'insurer_number',
      width: 100,
      onCell: r => ({ className: cellClass(r.colors?.insurer_number) }),
    },
    {
      title: '負担割合',
      dataIndex: 'patient_cost_rate',
      key: 'patient_cost_rate',
      width: 80,
      align: 'left',
      onCell: r => ({ className: cellClass(r.colors?.patient_cost_rate) }),
    },
    {
      title: '資格取得日',
      dataIndex: 'qualification_date',
      key: 'qualification_date',
      width: 130,
      align: 'left',
      onCell: r => ({ className: cellClass(r.colors?.qualification_date) }),
    },
    {
      title: '住所',
      key: 'address',
      width: 180,
      render: (_, record) =>
        renderTableCellTruncate(`${record.address1 || ''} ${record.address2 || ''}`.trim(), {
          width: 180,
          lineClamp: 2,
        }),
      onCell: r => ({ className: cellClass(r.colors?.address) }),
    },
    {
      title: '有効期限',
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      width: 130,
      align: 'left',
      onCell: r => ({ className: cellClass(r.colors?.expiration_date) }),
    },
  ];

  if (!isOpen) return null;

  const selectedId = selectedRowKeys[0] as number | undefined;

  return (
    <AppModal isOpen={isOpen} onClose={onClose} title="保険証情報チェック" maxWidth={1300}>
      <div className="flex flex-col max-h-[80vh]">
        <div className="relative flex flex-col flex-1 mb-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-full py-10">
              <AppSpin size="large" />
            </div>
          ) : (
            <AppTable<ComparedPatient>
              columns={columns}
              dataSource={patients.map(p => ({ ...p, key: p.id }))}
              pagination={false}
              bordered
              rowClassName={record =>
                `cursor-pointer ${record.id === selectedId ? 'cert-row-selected' : ''}`
              }
              onRow={record => ({
                onClick: () => setSelectedRowKeys([record.id]),
              })}
              scroll={{ y: 300, x: 1200 }}
              tableLayout="fixed"
              recordCount={patients.length}
            />
          )}
        </div>

        <div className="mb-4 space-y-1 text-xs text-gray-500">
          <p>※ すでに保険証情報があります。生年月日・性別以外の内容を確認してください。</p>
          <p>
            ※ <span className="px-1 bg-blue-200">水色の背景</span>
            は保険証履歴の資格取得から有効期限までの期間が重複しているため、履歴作成は出来ません。
          </p>
          <p>
            ※ <span className="px-1 bg-pink-100">ピンクの背景</span>
            は登録されている情報と差異があります。
          </p>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <div className="flex gap-2">
            <AppButton onClick={onClose}>閉じる</AppButton>
            <AppButton onClick={onCreateNew}>受診者新規登録</AppButton>
            <AppButton
              type="primary"
              onClick={() => selectedId != null && onSelectPatient(selectedId)}
              disabled={selectedId == null}
            >
              保険証画面へコピー (受診者上書登録)
            </AppButton>
            <AppButton
              type="primary"
              onClick={() => selectedId != null && onCopyWithHistory(selectedId)}
              disabled={isHistoryDisabled}
            >
              保険証画面へコピー (受診者履歴作成)
            </AppButton>
          </div>
        </div>
      </div>
    </AppModal>
  );
};

export default PatientDuplicateModal;
